import { pushLogEntry, LogLevel } from './debug-log/ring-buffer';

export class Logger {
	prefix: string;
	colors: Colors;
	constructor(prefix: string, colors?: Colors) {
		this.prefix = prefix;
		this.colors = {
			log: colors?.log,
			debug: colors?.debug,
			info: colors?.info,
			error: colors?.error,
			warn: colors?.warn,
		};
	}
	log(...msg: unknown[]) {
		const color = this.colors.log || '#bf5af2';
		console.log(`%c${this.prefix}`, Logger.logStyle(color), ...msg);
		this.record('log', msg);
	}
	info(...msg: unknown[]) {
		const color = this.colors.info || '#fed709';
		console.info(`%c${this.prefix}`, Logger.logStyle(color), ...msg);
		this.record('info', msg);
	}

	debug(...msg: unknown[]) {
		const color = this.colors.debug || '#5e5ce6';
		console.debug(`%c${this.prefix}`, Logger.logStyle(color), ...msg);
		this.record('debug', msg);
	}

	warn(...msg: unknown[]) {
		const color = this.colors.warn || 'rgb(255,204,0)';
		console.warn(`%c${this.prefix}`, Logger.logStyle(color), ...msg);
		this.record('warn', msg);
	}

	error(...msg: unknown[]) {
		const color = this.colors.error || 'rgb(255, 45, 85)';
		console.error(`%c${this.prefix}`, Logger.logStyle(color), ...msg);
		this.record('error', msg);
	}

	static logStyle(color: string) {
		return `color: white; background: ${color}; border-radius: 6px; padding: 2px 12px;`;
	}

	// Ring-buffer breadcrumb, one per call -- feeds the future devtools
	// panel and zip export (improvements/17-debug-capture-export.md). Only
	// warn/error carry a stack (captured at the call site, or lifted from
	// an Error argument if one was passed): allocating one on every noisy
	// `debug` call in a chatty template would be wasted work for entries
	// that are rarely the thing worth tracing back.
	private record(level: LogLevel, msg: Array<unknown>) {
		const isReportable = level === 'warn' || level === 'error';
		const errorArg = msg.find((m): m is Error => m instanceof Error);
		const stack = isReportable
			? (errorArg?.stack ?? new Error().stack)
			: undefined;

		// A lone string keeps its plain shape (the common case); anything
		// else -- multiple args, or a single non-string one -- keeps the
		// original argument list, same as what console.log itself received.
		const message =
			msg.length === 1 && typeof msg[0] === 'string' ? msg[0] : msg;

		pushLogEntry({
			timestamp: Date.now(),
			level,
			source: this.prefix,
			message,
			stack,
			// Only for errors: this is the call site of `logger.error(...)`
			// itself, not an Error argument's own throw site -- deliberately
			// so you can check out the version this log came from and see
			// the exact line that logged it.
			location: level === 'error' ? getCallSiteLocation() : undefined,
		});
	}
}

// Skips frames inside this file (record()/error()/warn()/etc themselves)
// to find the first real caller, then pulls a `path/to/file.ext:line` out
// of it. Prefers a `/src/...` path (meaningful in dev, where stacks point
// at real source files); falls back to whatever filename:line the frame
// has otherwise (a minified chunk name in a production build, still
// useful, just not a source path).
function getCallSiteLocation(): string | undefined {
	const stack = new Error().stack;
	if (!stack) {
		return undefined;
	}

	for (const line of stack.split('\n').slice(1)) {
		if (line.includes('/logger.ts')) {
			continue;
		}
		const srcMatch = line.match(/(src\/[^\s):]+\.tsx?):(\d+)/);
		if (srcMatch) {
			return `${srcMatch[1]}:${srcMatch[2]}`;
		}
		const fallbackMatch = line.match(/([^/\\\s():]+\.[jt]sx?):(\d+)/);
		if (fallbackMatch) {
			return `${fallbackMatch[1]}:${fallbackMatch[2]}`;
		}
	}
	return undefined;
}

interface Colors {
	log?: string;
	info?: string;
	debug?: string;
	error?: string;
	warn?: string;
}
